import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { calendarService } from '@/features/calendar/services/calendar.service';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/calendar-oauth-callback')({
  component: CalendarOAuthCallbackPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      code: (search.code as string) || null,
      error: (search.error as string) || null,
    };
  },
});

function CalendarOAuthCallbackPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { code, error } = useSearch({ from: '/calendar-oauth-callback' });
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const hasProcessedRef = useRef(false);

  const connectMutation = useMutation({
    mutationFn: (authCode: string) => calendarService.connectIntegration({ code: authCode }),
    onSuccess: () => {
      setStatus('success');
      toast({
        title: 'Integração conectada',
        description: 'Sua conta Google Calendar foi conectada com sucesso!',
      });
      setTimeout(() => {
        navigate({ to: '/calendar-config' });
      }, 2000);
    },
    onError: (error: any) => {
      setStatus('error');
      toast({
        title: 'Erro ao conectar',
        description: error.message || 'Não foi possível conectar a conta Google Calendar.',
        variant: 'destructive',
      });
      setTimeout(() => {
        navigate({ to: '/calendar-config' });
      }, 3000);
    },
  });

  useEffect(() => {
    // Prevenir execução múltipla
    if (hasProcessedRef.current) {
      return;
    }

    if (error) {
      hasProcessedRef.current = true;
      setStatus('error');
      toast({
        title: 'Erro na autorização',
        description: 'A autorização foi negada ou ocorreu um erro.',
        variant: 'destructive',
      });
      setTimeout(() => {
        navigate({ to: '/calendar-config' });
      }, 3000);
      return;
    }

    if (code && !connectMutation.isPending) {
      hasProcessedRef.current = true;
      connectMutation.mutate(code);
    }
  }, [code, error, navigate, toast, connectMutation]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          {status === 'loading' && (
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              <p className="text-muted-foreground">Conectando sua conta Google Calendar...</p>
            </div>
          )}
          {status === 'success' && (
            <div className="text-center space-y-4">
              <div className="text-green-500 text-4xl">✓</div>
              <p className="text-lg font-medium">Conta conectada com sucesso!</p>
              <p className="text-sm text-muted-foreground">Redirecionando...</p>
            </div>
          )}
          {status === 'error' && (
            <div className="text-center space-y-4">
              <div className="text-red-500 text-4xl">✗</div>
              <p className="text-lg font-medium">Erro ao conectar</p>
              <p className="text-sm text-muted-foreground">Redirecionando...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
